/**
 * @module Sagas/Collectors
 * @desc All Collectors sagas
 */
// Common components
import { Notification } from "src/components/Notification";
// Utilities
import { all, call, put, select, takeLatest } from "redux-saga/effects";
import { read } from "src/helpers/localStorage";
import { getDefaultDataVersions, getTsNumbersVersionsDataFromDocument } from "src/helpers/documentDataVersion";
import { downloadFile } from "src/helpers";
import * as R from "ramda";
import i18n from "src/locales";
// Services
import CollectorsService from "src/services/http/endpoints/collectors";
// Actions
import CollectorsActions from "./collectors.actions";
// Types
import { Action } from "src/types/common";
import type { StateNetwork } from "../index.reducer";
import type { CollectorsState } from "./collectors.reducer";
// Constants
import * as types from "./collectors.constants";

function* getTableCollectors() {
	try {
		yield put(
			CollectorsActions.setLoading({
				type: "tableCollectors",
				status: false,
			}),
		);

		const result = yield call(() => CollectorsService.getTableCollectors());
		const tableCollectors = result.data?.payload?.documents;

		yield put(CollectorsActions.setTableCollectors(tableCollectors));

		const documents = tableCollectors.map((collector) => collector.document);

		yield put(CollectorsActions.setCollectorDocuments(documents));

		const tsNumberAndVersionsData = getTsNumbersVersionsDataFromDocument(documents);
		const { groupWithDedicatedDataTypes } = tsNumberAndVersionsData;

		yield put(CollectorsActions.setDataVersionsList(groupWithDedicatedDataTypes));

		const defaultDataVersion = getDefaultDataVersions(groupWithDedicatedDataTypes);

		const { defaultGroup, defaultDataType, defaultVersion } = defaultDataVersion;

		const currentTableCollector = read("tableCollectorDataVersion");

		if (!currentTableCollector) {
			yield put(CollectorsActions.setSelectedDocument(documents[0] ?? {}));
		}
		yield put(CollectorsActions.setSelectedGroup(currentTableCollector?.Group ?? defaultGroup));
		yield put(CollectorsActions.setSelectedDataType(currentTableCollector?.DataType ?? defaultDataType));
		yield put(CollectorsActions.setSelectedVersion(currentTableCollector?.Version ?? defaultVersion));
		yield put(CollectorsActions.setVersionList([defaultVersion]));

		yield put(
			CollectorsActions.setLoading({
				type: "tableCollectors",
				status: false,
			}),
		);
	} catch (error) {
		if (R.includes(error?.message, ["timeout", "CORS"])) {
			Notification({ message: i18n.t("common.serverError"), type: "error" });
			yield put(
				CollectorsActions.setLoading({
					type: "tableCollectors",
					status: false,
				}),
			);
		}
		yield put(
			CollectorsActions.setLoading({
				type: "tableCollectors",
				status: false,
			}),
		);
		console.log("DEBUG ~ file: collectors.sagas.ts ~ error", error);
	}
}

function* getExteractedTableCollector(action: Action<any>) {
	try {
		yield put(
			CollectorsActions.setLoading({
				type: "extractedTableCollector",
				status: true,
			}),
		);

		if (action.payload) {
			const result = yield call(() => CollectorsService.getExtractedTableCollector(action.payload));
			const extractedTableCollector = result.data?.payload?.table;

			yield put(CollectorsActions.setExtractedTableCollector(extractedTableCollector));
		} else {
			yield put(CollectorsActions.setExtractedTableCollector(null));
		}

		yield put(
			CollectorsActions.setLoading({
				type: "extractedTableCollector",
				status: false,
			}),
		);
	} catch (error) {
		if (R.includes(error?.message, ["timeout", "CORS"])) {
			Notification({ message: i18n.t("common.serverError"), type: "error" });
			yield put(
				CollectorsActions.setLoading({
					type: "extractedTableCollector",
					status: false,
				}),
			);
		}
		yield put(
			CollectorsActions.setLoading({
				type: "extractedTableCollector",
				status: false,
			}),
		);
		console.log("DEBUG ~ file: collectors.sagas.ts ~ error", error);
	}
}

function* getEntities(action: any) {
	try {
		yield put(CollectorsActions.setLoading({ type: "entities", status: true }));
		const result = yield call(() => CollectorsService.getEntities());
		const payload = result.data?.payload?.entities || result.data?.entities || [];
		yield put(CollectorsActions.setEntities(payload));
		if (action.callback) {
			action.callback(result.data);
		}
	} catch (error) {
		console.error("Sagas/getEntities error:", error);
	} finally {
		yield put(CollectorsActions.setLoading({ type: "entities", status: false }));
	}
}

function* getDBColumns(action: any) {
	try {
		yield put(CollectorsActions.setLoading({ type: "dbColumns", status: true }));
		const result = yield call(() => CollectorsService.getDBColumns(action.payload));
		const payload = result.data?.payload?.dbColumns || result.data?.dbColumns || [];
		yield put(CollectorsActions.setDBColumns(payload));
		if (action.callback) {
			action.callback(result.data);
		}
	} catch (error) {
		console.error("Sagas/getDBColumns error:", error);
	} finally {
		yield put(CollectorsActions.setLoading({ type: "dbColumns", status: false }));
	}
}

function* getPreprocessingFunctions(action: any) {
	try {
		yield put(CollectorsActions.setLoading({ type: "preprocessingFunctions", status: true }));
		const result = yield call(() => CollectorsService.getPreprocessingFunctions());
		const payload =
			result.data?.payload?.preprocessingFunctionsNames || result.data?.preprocessingFunctionsNames || [];
		yield put(CollectorsActions.setPreprocessingFunctions(payload));
		if (action.callback) {
			action.callback(result.data);
		}
	} catch (error) {
		console.error("Sagas/getPreprocessingFunctions error:", error);
	} finally {
		yield put(CollectorsActions.setLoading({ type: "preprocessingFunctions", status: false }));
	}
}

function* getAutoExtractorConfigs(action: any) {
	try {
		yield put(CollectorsActions.setLoading({ type: "documentConfigs", status: true }));
		const result = yield call(() => CollectorsService.getAutoExtractorConfigs(action.payload));
		const payload = result.data?.payload?.configs || result.data?.configs || [];
		yield put(CollectorsActions.setDocumentConfigs(payload));
		if (action.callback) {
			action.callback(result.data);
		}
	} catch (error) {
		console.error("Sagas/getAutoExtractorConfigs error:", error);
	} finally {
		yield put(CollectorsActions.setLoading({ type: "documentConfigs", status: false }));
	}
}

function* saveAutoExtractorConfig(action: any) {
	try {
		const { id, payload } = action.payload;
		const result = id
			? yield call(() => CollectorsService.putAutoExtractorConfig(id, payload))
			: yield call(() => CollectorsService.postAutoExtractorConfigs(payload));

		if (action.callback) {
			action.callback(result.data);
		}
	} catch (error) {
		if (action.errorCallback) {
			action.errorCallback(error);
		}
	}
}

function* postAutoExtractorPreview(action: any) {
	try {
		const { id, type } = action.payload;
		const result = yield call(() => CollectorsService.postAutoExtractorPreview(id, type));
		if (action.callback) {
			action.callback(result.data);
		}
	} catch (error) {
		if (action.errorCallback) {
			action.errorCallback(error);
		}
	}
}

function* getAutoExtractorPreviewJobDetails(action: any) {
	try {
		const result = yield call(() => CollectorsService.getAutoExtractorPreviewJobDetails(action.payload));
		if (action.callback) {
			action.callback(result.data);
		}
	} catch (error) {
		if (action.errorCallback) {
			action.errorCallback(error);
		}
	}
}

function* exportTable(action: Action<"excel" | "json">) {
	try {
		const collectorsStore: CollectorsState = yield select((state: StateNetwork) => state.collectors);
		const tableId = collectorsStore.selectedTableCollector;
		if (!tableId) {
			return;
		}

		yield put(
			CollectorsActions.setLoading({
				type: "export",
				status: true,
			}),
		);

		const format = action.payload ?? "excel";
		const result = yield call(() => CollectorsService.exportTable(tableId, format));

		if (format === "json") {
			const payload = result.data;
			const baseName =
				result.headers?.["content-disposition"]?.split('"')?.[1]?.replace(/\.json$/i, "") ||
				`Simplexity_TC_${tableId}_export`;
			downloadFile(JSON.stringify(payload, null, 2), `${baseName}.json`, "application/json;charset=utf-8");
		} else {
			downloadFile(
				result.data,
				result.headers["content-disposition"].split('"')[1],
				"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8",
			);
		}
	} catch (error) {
		console.log("DEBUG ~ file: collectors.sagas.ts ~ exportTable ~ error", error);
		if (R.includes(error?.message, ["timeout", "CORS"])) {
			Notification({ message: i18n.t("common.serverError"), type: "error" });
		}
	} finally {
		yield put(
			CollectorsActions.setLoading({
				type: "export",
				status: false,
			}),
		);
	}
}

export default function* networkListeners(): Generator {
	yield all([
		takeLatest(types.SAGAS_GET_TABLE_COLLECTORS, getTableCollectors),
		takeLatest(types.SAGAS_GET_EXTRACTED_TABLE_COLLECTOR, getExteractedTableCollector),

		// NEW LISTENERS MOUNT
		takeLatest(types.SAGAS_GET_ENTITIES, getEntities),
		takeLatest(types.SAGAS_GET_DB_COLUMNS, getDBColumns),
		takeLatest(types.SAGAS_GET_PREPROCESSING_FUNCTIONS, getPreprocessingFunctions),
		takeLatest(types.SAGAS_GET_AUTO_EXTRACTOR_CONFIGS, getAutoExtractorConfigs),
		takeLatest(types.SAGAS_SAVE_AUTO_EXTRACTOR_CONFIG, saveAutoExtractorConfig),
		takeLatest(types.SAGAS_POST_AUTO_EXTRACTOR_PREVIEW, postAutoExtractorPreview),
		takeLatest(types.SAGAS_GET_AUTO_EXTRACTOR_PREVIEW_JOB, getAutoExtractorPreviewJobDetails),
		takeLatest(types.SAGAS_EXPORT_TABLE, exportTable),
	]);
}
